import { useEffect } from 'react';

import { socket } from '../libs/socket';

import { Trajectory, normalizeTrajectory } from './useDpdgState';

interface ISyncData {
  trajectory?: Trajectory;
  bgColor?: string;
  ballColor?: string;
  velocity?: number;
  backgroundType?: 'none' | 'video' | 'image';
  backgroundValue?: string;
  moving?: boolean;
  ballSize: number;
}

export const applyDpdgSyncAction = (
  action: string,
  data: Record<string, unknown>,
  onUpdate: (patch: Partial<ISyncData>) => void,
) => {
  switch (action) {
    case 'change_trajectory':
      if (typeof data.trajectory === 'string') onUpdate({ trajectory: normalizeTrajectory(data.trajectory) });
      break;
    case 'change_bg_color':
      if (typeof data.bgColor === 'string') onUpdate({ bgColor: data.bgColor });
      break;
    case 'change_ball_color':
      if (typeof data.ballColor === 'string') onUpdate({ ballColor: data.ballColor });
      break;
    case 'change_velocity':
      if (typeof data.velocity === 'number') onUpdate({ velocity: data.velocity });
      break;
    case 'change_background': {
      const patch: Partial<ISyncData> = {};
      if (typeof data.backgroundType === 'string') {
        patch.backgroundType = data.backgroundType as 'none' | 'video' | 'image';
        patch.backgroundValue = typeof data.backgroundValue === 'string' ? data.backgroundValue : '';
      }
      const bgColor = typeof data.bgColor === 'string' ? data.bgColor : typeof data.color === 'string' ? data.color : undefined;
      if (bgColor) {
        patch.bgColor = bgColor;
      }
      if (Object.keys(patch).length > 0) {
        onUpdate(patch);
      }
      break;
    }
    case 'change_ball_size':
      if (typeof data.ballSize === 'number') onUpdate({ ballSize: data.ballSize });
      break;
    case 'ball_start':
    case 'ball_stop':
      onUpdate({
        moving: action === 'ball_start',
        trajectory: typeof data.trajectory === 'string' ? normalizeTrajectory(data.trajectory) : undefined,
        bgColor: typeof data.bgColor === 'string' ? data.bgColor : undefined,
        ballColor: typeof data.ballColor === 'string' ? data.ballColor : undefined,
        velocity: typeof data.velocity === 'number' ? data.velocity : undefined,
        backgroundType:
          typeof data.backgroundType === 'string' ? (data.backgroundType as 'none' | 'video' | 'image') : undefined,
        backgroundValue: typeof data.backgroundValue === 'string' ? data.backgroundValue : '',
        ballSize: typeof data.ballSize === 'number' ? data.ballSize : undefined,
      });
      break;
    default:
      break;
  }
};

export const useSocketDpdg = (
  isPsy: boolean,
  roomId: string,
  onUpdate: (data: Partial<ISyncData>) => void,
  options?: {
    socketDisabled?: boolean;
    publishAction?: (action: string, data: Record<string, unknown>) => void;
  },
) => {
  const socketDisabled = options?.socketDisabled ?? false;
  const publishAction = options?.publishAction;

  useEffect(() => {
    if (socketDisabled || publishAction || !roomId) return;

    socket.emit('request_sync', { roomId });

    const handleMessage = (payload: { message: { action: string; data: any } }) => {
      const { action, data } = payload.message;
      applyDpdgSyncAction(action, data ?? {}, onUpdate);
    };

    const handleSync = (data: ISyncData) => {
      onUpdate({
        trajectory: data.trajectory || 'horizontal',
        bgColor: data.bgColor || '#F3F4F6',
        ballColor: data.ballColor || '#3b65b9',
        velocity: data.velocity || 0.2,
        backgroundType: data.backgroundType || 'none',
        backgroundValue: data.backgroundValue || '',
        moving: data.moving || false,
        ballSize: data.ballSize,
      });
    };

    socket.on('message', handleMessage);
    socket.on('sync_state', handleSync);

    socket.on('connect_error', () => {
      console.error('WebSocket connection failed');
    });

    return () => {
      socket.off('message', handleMessage);
      socket.off('sync_state', handleSync);
      socket.off('connect_error');
    };
  }, [roomId, onUpdate, isPsy, socketDisabled, publishAction]);

  const emitChange = (action: string, data: Partial<ISyncData>) => {
    if (!isPsy || !roomId) return;
    if (publishAction) {
      publishAction(action, data as Record<string, unknown>);
      return;
    }
    if (socketDisabled) return;
    socket.emit('message', {
      roomId,
      message: { action, data, datetime: new Date().toISOString() },
    });
  };

  return { emitChange };
};
